import type { SessionLog } from '@/lib/sessionStorage';
import {
  loadProgressionState as loadProgressionStateFromDexie,
  saveProgressionState as saveProgressionStateToDexie,
} from '@/lib/progressionService';
import type { Pillar, DBProgressionState } from './db';
import { getUnlockedRankAvatars, getUnlockedRankTitles, getUnlockedPillarEmblems, AVATARS, TITLES, EMBLEMS, CALLING_CARDS } from './cosmetics';
import { applySessionCrossAppBonuses, type BonusResult } from './crossAppBonuses';
import { recordOpmodeSession, publishOpmodeSnapshot } from './crossAppSync';
import { getGunfighterBridgeSnapshot } from './gunfighterBridge';

export type { Pillar };

// Re-export for backwards compatibility
export type ProgressionStateV1 = Omit<DBProgressionState, 'userId' | 'syncStatus' | 'syncedAt' | 'updatedAt'>;

const STORAGE_KEY = 'opmodeProgression'; // DEPRECATED: kept for migration only

// Seasonal operation (simple “season pass”)
export const CURRENT_OPERATION = {
  id: 'op-winter-forge-2026w01',
  name: 'Operation Winter Forge',
  startISO: new Date('2026-01-01T00:00:00.000Z').toISOString(),
  endISO: new Date('2026-02-01T00:00:00.000Z').toISOString(),
  intelGoal: 5000,
};

export function getWeekKey(d: Date = new Date()): string {
  // ISO week key: YYYY-Www
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Thursday in current week decides the year.
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

export function intelForLevel(level: number): number {
  // Escalating but not insane: L1=500, L10≈1100, L25≈2000
  const l = Math.max(1, Math.floor(level));
  return Math.round(450 + l * 55 + Math.pow(l, 1.2) * 12);
}

export function getStreakTitle(streakDays: number): { title: string; badge: string } {
  const s = Math.max(0, Math.floor(streakDays));
  if (s >= 60) return { title: 'Iron Discipline', badge: '' };
  if (s >= 30) return { title: 'Relentless Operator', badge: '' };
  if (s >= 14) return { title: 'Hard Charger', badge: '' };
  if (s >= 7) return { title: 'Consistent Professional', badge: '' };
  if (s >= 3) return { title: 'Week Stacker', badge: '' };
  if (s >= 1) return { title: 'Back In Action', badge: '' };
  return { title: 'New Recruit', badge: '' };
}

function clampInt(n: number, min: number, max: number): number {
  const v = Math.floor(Number.isFinite(n) ? n : 0);
  return Math.max(min, Math.min(max, v));
}

/**
 * Compute mystery Intel drop bonus (10% chance, +100-300 bonus)
 * Uses session ID as seed for deterministic randomness
 */
function computeMysteryDrop(sessionId: string): number {
  // Use session ID hash for deterministic "random"
  let hash = 0;
  for (let i = 0; i < sessionId.length; i++) {
    hash = ((hash << 5) - hash) + sessionId.charCodeAt(i);
    hash |= 0;
  }
  // 10% chance (hash mod 10 === 0)
  if (Math.abs(hash) % 10 !== 0) return 0;
  // Random bonus between 100-300 based on hash
  const bonus = 100 + (Math.abs(hash) % 201);
  return bonus;
}

function computeSessionIntel(log: SessionLog, isFirstSessionOfDay: boolean = false): {
  total: number;
  pillars: Partial<Record<Pillar, number>>;
  breakdown: { base: number; perExercise: number; assessmentBonus: number; dailyBonus: number; mysteryDrop: number };
} {
  // Simple, feels-good formula:
  // - Base session intel
  // - + per completed exercise (capped)
  // - + assessment bonus
  // - + daily first session bonus (replaces gameable readiness bonus)
  // - + mystery Intel drop (10% chance, +100-300)
  const isPT = log.sessionType === 'pt' || log.sessionType === 'assessment';
  const exercises = (log as any).exercises as any[] | undefined;
  const completedExercises = Array.isArray(exercises) ? exercises.filter((e) => e?.completed) : [];

  const base = 120;
  const perExercise = clampInt(completedExercises.length * 12, 0, 120);
  const assessmentBonus = log.sessionType === 'assessment' ? 80 : 0;
  // Daily first session bonus replaces gameable readiness bonus
  const dailyBonus = isFirstSessionOfDay ? 25 : 0;
  // Mystery Intel drop (10% chance)
  const sessionId = String((log as any).id || Date.now().toString());
  const mysteryDrop = computeMysteryDrop(sessionId);

  const total = base + perExercise + assessmentBonus + dailyBonus + mysteryDrop;
  const breakdown = { base, perExercise, assessmentBonus, dailyBonus, mysteryDrop };

  // Pillar attribution from exercise types (approximate but motivating)
  const pillars: Partial<Record<Pillar, number>> = {};
  if (!isPT || !Array.isArray(exercises)) {
    pillars.consistency = Math.round(total * 0.5);
    return { total, pillars, breakdown };
  }

  const counts = { strength: 0, endurance: 0, bodyweight: 0, recovery: 0, consistency: 1 };
  for (const ex of completedExercises) {
    const t = String(ex?.exerciseType || '').toLowerCase();
    const block = String(ex?.block || '').toLowerCase();
    if (block.includes('recovery') || block.includes('cool')) {
      counts.recovery += 1;
      continue;
    }
    // `circuit` and `interval` are conditioning, not strength, and a `carry` is
    // loaded conditioning — all three fell into the `else` and were credited to
    // the Strength pillar, overstating it and starving Endurance for anyone
    // whose training is circuit- or carry-heavy.
    if (t === 'ruck' || t === 'cardio' || t === 'swim-sets' || t === 'circuit' || t === 'interval' || t === 'carry') {
      counts.endurance += 1;
    }
    else if (t === 'bodyweight') counts.bodyweight += 1;
    else counts.strength += 1;
  }

  const totalBuckets = counts.strength + counts.endurance + counts.bodyweight + counts.recovery + counts.consistency;
  const share = (bucket: number) => (totalBuckets > 0 ? bucket / totalBuckets : 0);

  pillars.strength = Math.round(total * share(counts.strength));
  pillars.endurance = Math.round(total * share(counts.endurance));
  pillars.bodyweight = Math.round(total * share(counts.bodyweight));
  pillars.recovery = Math.round(total * share(counts.recovery));
  pillars.consistency = Math.round(total * share(counts.consistency));

  // Fix rounding drift: ensure sum equals total by adjusting consistency
  const sum =
    (pillars.strength || 0) +
    (pillars.endurance || 0) +
    (pillars.bodyweight || 0) +
    (pillars.recovery || 0) +
    (pillars.consistency || 0);
  const delta = total - sum;
  pillars.consistency = (pillars.consistency || 0) + delta;

  return { total, pillars, breakdown };
}

/**
 * Check if this is the first session of the day for the user
 */
function isFirstSessionOfDayCheck(state: DBProgressionState, sessionDate: Date): boolean {
  const todayStr = sessionDate.toDateString();
  // Check if any previously awarded session was from today
  // We track last session date in the state
  const lastSessionDate = state.lastSessionDate ? new Date(state.lastSessionDate).toDateString() : null;
  return lastSessionDate !== todayStr;
}

export interface IntelAwardResult {
  awarded: boolean;
  intelGained: number;
  newState: DBProgressionState;
  breakdown?: {
    base: number;
    perExercise: number;
    assessmentBonus: number;
    dailyBonus: number;
    mysteryDrop: number;
    crossAppBonus?: number;
  };
  leveledUp?: boolean;
  newLevel?: number;
  streakUpdated?: boolean;
  newStreak?: number;
  milestoneUnlocked?: string;
  crossAppBonuses?: BonusResult[];
}

export async function awardIntelForSession(userId: string, log: SessionLog): Promise<IntelAwardResult> {
  const state = await loadProgressionStateFromDexie(userId);
  const sessionId = String((log as any).id || '');
  if (!sessionId) {
    console.warn(' Session has no ID, cannot award intel');
    return { awarded: false, intelGained: 0, newState: state };
  }

  // Idempotency: do not award twice for the same session
  if (state.awardedSessionIds.includes(sessionId)) {
    console.log(`ℹ Session ${sessionId} already awarded intel, skipping`);
    return { awarded: false, intelGained: 0, newState: state };
  }

  // Check if this is the first session of the day
  const sessionDate = new Date(log.date);
  const isFirstOfDay = isFirstSessionOfDayCheck(state, sessionDate);

  const { total, pillars, breakdown } = computeSessionIntel(log, isFirstOfDay);

  // Check for cross-app bonuses
  let crossAppBonuses: BonusResult[] = [];
  let crossAppBonusIntel = 0;
  try {
    const crossAppResult = await applySessionCrossAppBonuses(userId, total, sessionDate);
    crossAppBonuses = crossAppResult.bonuses;
    crossAppBonusIntel = crossAppResult.bonusIntel;

    if (crossAppBonusIntel > 0) {
      console.log(` Cross-app bonus: +${crossAppBonusIntel} Intel`);
    }
  } catch (error) {
    console.warn(' Cross-app bonus check failed (non-critical):', error);
  }

  const grandTotal = total + crossAppBonusIntel;

  // Log breakdown for transparency
  console.log(` Awarding ${grandTotal} Intel for session ${sessionId}`);
  if (breakdown.mysteryDrop > 0) {
    console.log(` MYSTERY DROP! +${breakdown.mysteryDrop} bonus Intel!`);
  }
  if (breakdown.dailyBonus > 0) {
    console.log(` Daily first session bonus: +${breakdown.dailyBonus}`);
  }

  // Apply Intel (including cross-app bonus)
  state.lifetimeIntel += grandTotal;
  state.weeklyIntel += grandTotal;
  state.operation.intelEarned += grandTotal;

  // Pillar mastery intel
  (Object.keys(state.pillarIntel) as Pillar[]).forEach((p) => {
    state.pillarIntel[p] += pillars[p] || 0;
  });

  // Level up loop
  let intelRemaining = grandTotal;
  const startLevel = state.level;
  while (intelRemaining > 0) {
    const needed = intelForLevel(state.level) - state.levelIntel;
    if (intelRemaining >= needed) {
      state.level += 1;
      state.levelIntel = 0;
      intelRemaining -= needed;
      console.log(` Level up! Now level ${state.level}`);
    } else {
      state.levelIntel += intelRemaining;
      intelRemaining = 0;
    }
  }
  const leveledUp = state.level > startLevel;

  // Update streak tracking
  const todayStr = sessionDate.toISOString().split('T')[0];
  const yesterday = new Date(sessionDate);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  let streakUpdated = false;
  if (state.lastStreakDate === todayStr) {
    // Already counted toward streak today
  } else if (state.lastStreakDate === yesterdayStr) {
    // Consecutive day - increment streak
    state.streakDays = (state.streakDays || 0) + 1;
    state.lastStreakDate = todayStr;
    streakUpdated = true;
    if (state.streakDays > (state.longestStreak || 0)) {
      state.longestStreak = state.streakDays;
    }
    // Notify on milestone streaks
    if ([3, 5, 7, 14, 21, 30, 60, 90].includes(state.streakDays)) {
      import('./notifications').then(({ notifyStreakMilestone }) => notifyStreakMilestone(state.streakDays)).catch(() => {});
    }
  } else if (!state.lastStreakDate) {
    // First ever session
    state.streakDays = 1;
    state.lastStreakDate = todayStr;
    state.longestStreak = 1;
    streakUpdated = true;
  } else {
    // Streak broken - check for freeze protection or active break
    const daysSinceLastStreak = Math.floor(
      (sessionDate.getTime() - new Date(state.lastStreakDate).getTime()) / (1000 * 60 * 60 * 24)
    );

    // Streak freeze for a sanctioned break.
    //
    // Asks whether a break covered THE GAP, not whether one is live right now.
    // The live check never fired for the case it exists for: users return
    // home, tap "End Trip & Resume Program", and *then* train — so by the time
    // a session is logged the break is archived and `isOnActiveBreak` is
    // false. The travel banner promises "streak protection until your return"
    // and the streak reset anyway.
    let breakProtected = false;
    try {
      const { wasOnBreakBetween } = await import('./trainingBreak');
      if (await wasOnBreakBetween(userId, state.lastStreakDate, todayStr)) {
        breakProtected = true;
        console.log(`Training break covered the gap — streak protected (${state.streakDays} days)`);
        state.streakDays = (state.streakDays || 0) + 1;
        state.lastStreakDate = todayStr;
        streakUpdated = true;
      }
    } catch {
      // trainingBreak module not available — fall through
    }

    // Check if ACWR/recovery recommends rest — if so, protect the streak
    // for up to 2 missed days (same as a training break)
    let acwrProtected = false;
    if (!breakProtected && daysSinceLastStreak <= 3) {
      try {
        const { calculateRecoveryDebt } = await import('./recoveryDebt');
        const debt = await calculateRecoveryDebt(28);
        if (debt.needsDeload) {
          acwrProtected = true;
          console.log(`ACWR/recovery debt recommends rest — streak protected (${state.streakDays} days)`);
          state.streakDays = (state.streakDays || 0) + 1;
          state.lastStreakDate = todayStr;
          streakUpdated = true;
        }
      } catch { /* non-critical */ }
    }

    if (!breakProtected && !acwrProtected) {
      if (daysSinceLastStreak === 2 && !state.streakFreezeUsedThisMonth) {
        // Use freeze protection - streak continues
        console.log(` Streak freeze activated! Saved ${state.streakDays}-day streak`);
        state.streakFreezeUsedThisMonth = true;
        state.streakDays = (state.streakDays || 0) + 1;
        state.lastStreakDate = todayStr;
        streakUpdated = true;
      } else {
        // Streak broken, start new one
        console.log(` Streak broken after ${state.streakDays} days`);
        state.streakDays = 1;
        state.lastStreakDate = todayStr;
        streakUpdated = true;
      }
    }
  }

  // Update last session date for daily bonus tracking
  state.lastSessionDate = todayStr;

  // Check for milestones
  const milestone = checkMilestones(state);

  // Check for Intel-based cosmetic unlocks (modifies state directly)
  const avatarsUnlocked = checkIntelAvatarUnlocks(state);
  const titlesUnlocked = checkIntelTitleUnlocks(state);
  const emblemsUnlocked = checkPillarEmblemUnlocks(state);

  // Track session ids (cap list at 200)
  state.awardedSessionIds = [sessionId, ...state.awardedSessionIds].slice(0, 200);

  await saveProgressionStateToDexie(state);

  // Record session for cross-app tracking (non-blocking)
  recordOpmodeSession(userId, sessionDate).catch(err =>
    console.warn(' Cross-app session recording failed (non-critical):', err)
  );

  // Publish OPMODE snapshot for GunfighterOS bridge (non-blocking)
  publishOpmodeSnapshot(userId).catch(err =>
    console.warn(' OPMODE snapshot publish failed (non-critical):', err)
  );

  return {
    awarded: true,
    intelGained: grandTotal,
    newState: state,
    breakdown: {
      ...breakdown,
      crossAppBonus: crossAppBonusIntel,
    },
    leveledUp,
    newLevel: leveledUp ? state.level : undefined,
    streakUpdated,
    newStreak: streakUpdated ? state.streakDays : undefined,
    milestoneUnlocked: milestone,
    crossAppBonuses: crossAppBonuses.length > 0 ? crossAppBonuses : undefined,
  };
}

/**
 * Award Intel + update streak for a rest day.
 * Awards a small amount (10 Intel) and counts toward the streak,
 * without creating a full session log.
 */
export async function awardIntelForRestDay(
  userId: string,
  restDayId: string,
  didActiveRecovery: boolean = false,
): Promise<IntelAwardResult> {
  const state = await loadProgressionStateFromDexie(userId);

  // Idempotency
  if (state.awardedSessionIds.includes(restDayId)) {
    return { awarded: false, intelGained: 0, newState: state };
  }

  // Rest day Intel: 10 base + 5 if active recovery
  const intelGained = didActiveRecovery ? 15 : 10;

  state.lifetimeIntel += intelGained;
  state.weeklyIntel += intelGained;
  state.operation.intelEarned += intelGained;

  // Level up loop
  let intelRemaining = intelGained;
  const startLevel = state.level;
  while (intelRemaining > 0) {
    const needed = intelForLevel(state.level) - state.levelIntel;
    if (intelRemaining >= needed) {
      state.level += 1;
      state.levelIntel = 0;
      intelRemaining -= needed;
    } else {
      state.levelIntel += intelRemaining;
      intelRemaining = 0;
    }
  }
  const leveledUp = state.level > startLevel;

  // Update streak tracking (same logic as awardIntelForSession)
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  let streakUpdated = false;
  if (state.lastStreakDate === todayStr) {
    // Already counted today
  } else if (state.lastStreakDate === yesterdayStr) {
    state.streakDays = (state.streakDays || 0) + 1;
    state.lastStreakDate = todayStr;
    streakUpdated = true;
    if (state.streakDays > (state.longestStreak || 0)) {
      state.longestStreak = state.streakDays;
    }
  } else if (!state.lastStreakDate) {
    state.streakDays = 1;
    state.lastStreakDate = todayStr;
    state.longestStreak = 1;
    streakUpdated = true;
  } else {
    const daysSinceLastStreak = Math.floor(
      (now.getTime() - new Date(state.lastStreakDate).getTime()) / (1000 * 60 * 60 * 24)
    );

    // Same fix as above — protect on break OVERLAP with the gap, not on a
    // break being live at the moment this runs.
    let breakProtected = false;
    try {
      const { wasOnBreakBetween } = await import('./trainingBreak');
      if (await wasOnBreakBetween(userId, state.lastStreakDate, todayStr)) {
        breakProtected = true;
        state.streakDays = (state.streakDays || 0) + 1;
        state.lastStreakDate = todayStr;
        streakUpdated = true;
      }
    } catch {
      // trainingBreak module not available — fall through
    }

    if (!breakProtected) {
      if (daysSinceLastStreak === 2 && !state.streakFreezeUsedThisMonth) {
        state.streakFreezeUsedThisMonth = true;
        state.streakDays = (state.streakDays || 0) + 1;
        state.lastStreakDate = todayStr;
        streakUpdated = true;
      } else {
        state.streakDays = 1;
        state.lastStreakDate = todayStr;
        streakUpdated = true;
      }
    }
  }

  state.lastSessionDate = todayStr;
  state.awardedSessionIds = [restDayId, ...state.awardedSessionIds].slice(0, 200);

  await saveProgressionStateToDexie(state);

  return {
    awarded: true,
    intelGained,
    newState: state,
    leveledUp,
    newLevel: leveledUp ? state.level : undefined,
    streakUpdated,
    newStreak: streakUpdated ? state.streakDays : undefined,
  };
}

/**
 * Award bonus Intel (for achievements, cross-app bonuses, etc.)
 * This is a simpler function that just adds Intel without session tracking.
 */
export async function awardBonusIntel(
  userId: string,
  amount: number,
  reason: string
): Promise<{ success: boolean; newTotal: number }> {
  const state = await loadProgressionStateFromDexie(userId);

  const bonusAmount = Math.max(0, Math.floor(amount));
  if (bonusAmount === 0) {
    return { success: false, newTotal: state.lifetimeIntel };
  }

  console.log(` Awarding ${bonusAmount} bonus Intel: ${reason}`);

  // Apply Intel
  state.lifetimeIntel += bonusAmount;
  state.weeklyIntel += bonusAmount;
  state.operation.intelEarned += bonusAmount;

  // Level up loop
  let intelRemaining = bonusAmount;
  while (intelRemaining > 0) {
    const needed = intelForLevel(state.level) - state.levelIntel;
    if (intelRemaining >= needed) {
      state.level += 1;
      state.levelIntel = 0;
      intelRemaining -= needed;
      console.log(` Level up! Now level ${state.level}`);
    } else {
      state.levelIntel += intelRemaining;
      intelRemaining = 0;
    }
  }

  // Check for Intel-based cosmetic unlocks
  checkIntelAvatarUnlocks(state);
  checkIntelTitleUnlocks(state);
  checkPillarEmblemUnlocks(state);

  await saveProgressionStateToDexie(state);

  return { success: true, newTotal: state.lifetimeIntel };
}

// ============================================================================
// MILESTONES
// ============================================================================

interface Milestone {
  id: string;
  title: string;
  description: string;
  check: (state: DBProgressionState) => boolean;
}

const MILESTONES: Milestone[] = [
  {
    id: 'first-session',
    title: 'First Blood',
    description: 'Complete your first training session',
    check: (s) => s.lifetimeIntel > 0,
  },
  {
    id: '10-sessions',
    title: 'Getting Started',
    description: 'Complete 10 training sessions',
    check: (s) => s.awardedSessionIds.length >= 10,
  },
  {
    id: '50-sessions',
    title: 'Committed',
    description: 'Complete 50 training sessions',
    check: (s) => s.awardedSessionIds.length >= 50,
  },
  {
    id: '100-sessions',
    title: 'Century Club',
    description: 'Complete 100 training sessions',
    check: (s) => s.archetypeProgress['operator'].totalSessions >= 100,
  },
  {
    id: '1000-intel',
    title: 'Intel Operative',
    description: 'Earn 1,000 lifetime Intel',
    check: (s) => s.lifetimeIntel >= 1000,
  },
  {
    id: '5000-intel',
    title: 'Intelligence Officer',
    description: 'Earn 5,000 lifetime Intel',
    check: (s) => s.lifetimeIntel >= 5000,
  },
  {
    id: '10000-intel',
    title: 'Spymaster',
    description: 'Earn 10,000 lifetime Intel',
    check: (s) => s.lifetimeIntel >= 10000,
  },
  {
    id: '7-day-streak',
    title: 'Week Warrior',
    description: 'Maintain a 7-day training streak',
    check: (s) => (s.streakDays || 0) >= 7,
  },
  {
    id: '14-day-streak',
    title: 'Fortnight Fighter',
    description: 'Maintain a 14-day training streak',
    check: (s) => (s.streakDays || 0) >= 14,
  },
  {
    id: '30-day-streak',
    title: 'Monthly Master',
    description: 'Maintain a 30-day training streak',
    check: (s) => (s.streakDays || 0) >= 30,
  },
  {
    id: '60-day-streak',
    title: 'Iron Discipline',
    description: 'Maintain a 60-day training streak',
    check: (s) => (s.streakDays || 0) >= 60,
  },
  {
    id: 'level-10',
    title: 'Operator Candidate',
    description: 'Reach level 10',
    check: (s) => s.level >= 10,
  },
  {
    id: 'level-25',
    title: 'Seasoned Operator',
    description: 'Reach level 25',
    check: (s) => s.level >= 25,
  },
  {
    id: 'level-50',
    title: 'Elite Operator',
    description: 'Reach level 50',
    check: (s) => s.level >= 50,
  },
  {
    id: 'first-archetype',
    title: 'Identity Found',
    description: 'Unlock your first archetype',
    check: (s) => s.unlockedArchetypes.length >= 1,
  },
  {
    id: 'all-archetypes',
    title: 'Master of All',
    description: 'Unlock all 5 archetypes',
    check: (s) => s.unlockedArchetypes.length >= 5,
  },
  {
    id: 'balanced-intel',
    title: 'Balanced Operator',
    description: 'Earn 500+ Intel in all 5 pillars',
    check: (s) =>
      s.pillarIntel.strength >= 500 &&
      s.pillarIntel.endurance >= 500 &&
      s.pillarIntel.bodyweight >= 500 &&
      s.pillarIntel.recovery >= 500 &&
      s.pillarIntel.consistency >= 500,
  },
];

function checkMilestones(state: DBProgressionState): string | undefined {
  const unlocked = state.milestonesUnlocked || [];

  for (const milestone of MILESTONES) {
    if (!unlocked.includes(milestone.id) && milestone.check(state)) {
      console.log(` MILESTONE UNLOCKED: ${milestone.title}`);
      state.milestonesUnlocked = [...unlocked, milestone.id];
      return milestone.id;
    }
  }

  return undefined;
}

export function getMilestones(): Milestone[] {
  return MILESTONES;
}

// ============================================================================
// INTEL-BASED AVATAR UNLOCKS
// ============================================================================

/**
 * Check and unlock rank-based avatars when lifetime Intel thresholds are reached
 * IMPORTANT: Modifies the state object directly to avoid overwrites
 */
function checkIntelAvatarUnlocks(state: DBProgressionState): string[] {
  const earnedAvatars = getUnlockedRankAvatars(state.lifetimeIntel);
  const newlyUnlocked: string[] = [];

  // Initialize array if needed
  if (!state.unlockedAvatars) {
    state.unlockedAvatars = ['default'];
  }

  for (const avatarId of earnedAvatars) {
    if (!state.unlockedAvatars.includes(avatarId)) {
      state.unlockedAvatars.push(avatarId);
      newlyUnlocked.push(avatarId);
      console.log(` Rank avatar unlocked: ${avatarId} (Lifetime Intel: ${state.lifetimeIntel})`);
    }
  }

  return newlyUnlocked;
}

/**
 * Check and unlock rank-based titles when lifetime Intel thresholds are reached
 * IMPORTANT: Modifies the state object directly to avoid overwrites
 */
function checkIntelTitleUnlocks(state: DBProgressionState): string[] {
  const earnedTitles = getUnlockedRankTitles(state.lifetimeIntel);
  const newlyUnlocked: string[] = [];

  // Initialize array if needed
  if (!state.unlockedTitles) {
    state.unlockedTitles = ['Recruit'];
  }

  for (const titleId of earnedTitles) {
    if (!state.unlockedTitles.includes(titleId)) {
      state.unlockedTitles.push(titleId);
      newlyUnlocked.push(titleId);
      console.log(` Rank title unlocked: ${titleId} (Lifetime Intel: ${state.lifetimeIntel})`);
    }
  }

  return newlyUnlocked;
}

/**
 * Check and unlock pillar emblems when pillar Intel thresholds are reached
 * IMPORTANT: Modifies the state object directly to avoid overwrites
 */
function checkPillarEmblemUnlocks(state: DBProgressionState): string[] {
  const earnedEmblems = getUnlockedPillarEmblems(state.pillarIntel);
  const newlyUnlocked: string[] = [];

  // Initialize array if needed
  if (!state.unlockedEmblems) {
    state.unlockedEmblems = ['default'];
  }

  for (const emblemId of earnedEmblems) {
    if (!state.unlockedEmblems.includes(emblemId)) {
      state.unlockedEmblems.push(emblemId);
      newlyUnlocked.push(emblemId);
      console.log(` Pillar emblem unlocked: ${emblemId}`);
    }
  }

  return newlyUnlocked;
}

/**
 * Check and unlock cross-app cosmetics based on GunfighterOS integration
 * IMPORTANT: Modifies the state object directly to avoid overwrites
 */
async function checkCrossAppCosmeticUnlocks(state: DBProgressionState, userId: string): Promise<{
  avatars: string[];
  titles: string[];
  emblems: string[];
  callingCards: string[];
}> {
  const result = { avatars: [] as string[], titles: [] as string[], emblems: [] as string[], callingCards: [] as string[] };

  // Not linked? No cross-app cosmetics
  if (!state.gunfighterUserId) {
    return result;
  }

  // Initialize arrays if needed
  if (!state.unlockedAvatars) state.unlockedAvatars = ['default'];
  if (!state.unlockedTitles) state.unlockedTitles = ['Recruit'];
  if (!state.unlockedEmblems) state.unlockedEmblems = ['default'];
  if (!state.unlockedCallingCards) state.unlockedCallingCards = ['default'];

  // Get GunfighterOS data
  let shooterGrade = 0;
  try {
    const snapshot = await getGunfighterBridgeSnapshot(userId);
    if (snapshot) {
      shooterGrade = snapshot.shooterGrade?.overall || 0;
    }
  } catch {
    // Use cached data or 0
  }

  const operatorScore = state.operatorScore || 0;
  const dualTrainingWeeks = state.dualTrainingWeeks || 0;
  const combinedScore = operatorScore + shooterGrade;

  // Check all cross-app cosmetics
  const checkUnlock = (
    cosmetic: { id: string; unlockRequirement?: { type: string; value: number; crossappType?: string } },
    unlockedArray: string[],
    newlyUnlockedArray: string[],
    type: string
  ) => {
    if (unlockedArray.includes(cosmetic.id)) return;

    const req = cosmetic.unlockRequirement;
    if (!req || req.type !== 'crossapp') return;

    let isUnlocked = false;

    switch (req.crossappType) {
      case 'link':
        isUnlocked = true; // Already linked to get here
        break;
      case 'dual-weeks':
        isUnlocked = dualTrainingWeeks >= req.value;
        break;
      case 'shooter-grade':
        isUnlocked = shooterGrade >= req.value;
        break;
      case 'combined-score':
        isUnlocked = combinedScore >= req.value;
        break;
      case 'both-elite':
        isUnlocked = operatorScore >= req.value && shooterGrade >= req.value;
        break;
    }

    if (isUnlocked) {
      unlockedArray.push(cosmetic.id);
      newlyUnlockedArray.push(cosmetic.id);
      console.log(` Cross-app ${type} unlocked: ${cosmetic.id}`);
    }
  };

  // Check avatars
  Object.values(AVATARS)
    .filter(a => a.category === 'crossapp')
    .forEach(a => checkUnlock(a, state.unlockedAvatars!, result.avatars, 'avatar'));

  // Check titles
  Object.values(TITLES)
    .filter(t => t.category === 'crossapp')
    .forEach(t => checkUnlock(t, state.unlockedTitles!, result.titles, 'title'));

  // Check emblems
  Object.values(EMBLEMS)
    .filter(e => e.category === 'crossapp')
    .forEach(e => checkUnlock(e, state.unlockedEmblems!, result.emblems, 'emblem'));

  // Check calling cards
  Object.values(CALLING_CARDS)
    .filter(c => c.category === 'crossapp')
    .forEach(c => checkUnlock(c, state.unlockedCallingCards!, result.callingCards, 'calling card'));

  return result;
}

// ============================================================================
// OPERATOR SCORE MILESTONES
// ============================================================================

export interface OperatorScoreMilestone {
  id: string;
  title: string;
  description: string;
  operatorScore: number;  // Threshold to achieve
  intelReward: number;    // Intel bonus awarded
}

export const OPERATOR_SCORE_MILESTONES: OperatorScoreMilestone[] = [
  {
    id: 'os-40',
    title: 'Foundation Builder',
    description: 'Reach Operator Score 40 for the first time',
    operatorScore: 40,
    intelReward: 200,
  },
  {
    id: 'os-50',
    title: 'Solid Performer',
    description: 'Reach Operator Score 50 for the first time',
    operatorScore: 50,
    intelReward: 300,
  },
  {
    id: 'os-60',
    title: 'Above Average',
    description: 'Reach Operator Score 60 for the first time',
    operatorScore: 60,
    intelReward: 400,
  },
  {
    id: 'os-70',
    title: 'High Performer',
    description: 'Reach Operator Score 70 for the first time',
    operatorScore: 70,
    intelReward: 500,
  },
  {
    id: 'os-80',
    title: 'Elite Status',
    description: 'Reach Operator Score 80 for the first time',
    operatorScore: 80,
    intelReward: 750,
  },
  {
    id: 'os-90',
    title: 'Peak Performance',
    description: 'Reach Operator Score 90 for the first time',
    operatorScore: 90,
    intelReward: 1000,
  },
];

/**
 * Check and award Intel for Operator Score milestones
 * Call this when a new Operator Score is calculated
 * Returns the milestone achieved (if any) and Intel awarded
 */
export async function checkOperatorScoreMilestones(
  userId: string,
  operatorScore: number
): Promise<{ milestone: OperatorScoreMilestone | null; intelAwarded: number }> {
  const state = await loadProgressionStateFromDexie(userId);

  // Initialize tracking if needed
  if (!state.operatorScoreMilestonesUnlocked) {
    state.operatorScoreMilestonesUnlocked = [];
  }

  // Find the highest milestone achieved that hasn't been unlocked yet
  for (const milestone of OPERATOR_SCORE_MILESTONES) {
    if (
      operatorScore >= milestone.operatorScore &&
      !state.operatorScoreMilestonesUnlocked.includes(milestone.id)
    ) {
      // Award the Intel bonus
      state.lifetimeIntel += milestone.intelReward;
      state.weeklyIntel += milestone.intelReward;
      state.operation.intelEarned += milestone.intelReward;
      state.operatorScoreMilestonesUnlocked.push(milestone.id);

      console.log(` OPERATOR SCORE MILESTONE: ${milestone.title} - +${milestone.intelReward} Intel!`);

      // Check for avatar unlocks with new Intel (modifies state directly)
      checkIntelAvatarUnlocks(state);
      checkIntelTitleUnlocks(state);

      await saveProgressionStateToDexie(state);

      return { milestone, intelAwarded: milestone.intelReward };
    }
  }

  return { milestone: null, intelAwarded: 0 };
}

export function getOperatorScoreMilestones(): OperatorScoreMilestone[] {
  return OPERATOR_SCORE_MILESTONES;
}

/**
 * Sync all cosmetic unlocks for a user based on their current Intel
 * Call this to retroactively unlock items for users who earned Intel
 * before the unlock system was properly working
 */
export async function syncCosmeticUnlocks(userId: string): Promise<{
  avatarsUnlocked: string[];
  titlesUnlocked: string[];
  emblemsUnlocked: string[];
  callingCardsUnlocked: string[];
}> {
  const state = await loadProgressionStateFromDexie(userId);

  console.log(` Syncing cosmetic unlocks for user with ${state.lifetimeIntel} lifetime Intel`);

  // Check and unlock all earned cosmetics (modifies state directly)
  const avatarsUnlocked = checkIntelAvatarUnlocks(state);
  const titlesUnlocked = checkIntelTitleUnlocks(state);
  const emblemsUnlocked = checkPillarEmblemUnlocks(state);
  const callingCardsUnlocked: string[] = [];

  // Check cross-app cosmetics if linked to GunfighterOS
  const crossAppUnlocks = await checkCrossAppCosmeticUnlocks(state, userId);
  avatarsUnlocked.push(...crossAppUnlocks.avatars);
  titlesUnlocked.push(...crossAppUnlocks.titles);
  emblemsUnlocked.push(...crossAppUnlocks.emblems);
  callingCardsUnlocked.push(...crossAppUnlocks.callingCards);

  // Save state if anything was unlocked
  const totalUnlocks = avatarsUnlocked.length + titlesUnlocked.length + emblemsUnlocked.length + callingCardsUnlocked.length;
  if (totalUnlocks > 0) {
    await saveProgressionStateToDexie(state);
    console.log(` Synced unlocks: ${avatarsUnlocked.length} avatars, ${titlesUnlocked.length} titles, ${emblemsUnlocked.length} emblems, ${callingCardsUnlocked.length} calling cards`);
  }

  return { avatarsUnlocked, titlesUnlocked, emblemsUnlocked, callingCardsUnlocked };
}


